<template>
  <header
    :class="[
      'v-hero',
      {
        'v-hero--with-image': !!imgSrc,
        'v-hero--extra-blur': !!imgSrc && blur,
        'v-hero--auto-height': !!autoHeight,
        'v-hero--small': !!small,
        'v-hero--minimal': !!minimal,
        'v-hero--split': !!split,
      }
    ]"
  >
    <!-- Minimal hero -->
    <div v-if="minimal" class="v-hero__content container-fluid">
      <slot />
    </div>

    <!-- Split hero - image -->
    <div v-else-if="split && !video" class="container-fluid">
      <div
        class="v-hero--split__media"
        :style="[!!imgSrc && {backgroundImage: `url('${resolveUrl(imgSrc)}')`}
      ]"></div>
      <div class="v-hero--split__content">
        <slot />
      </div>
    </div>

    <!-- Split hero - video -->
    <div v-else-if="split && video" class="container-fluid">
      <slot />
    </div>

    <!-- Standard hero -->
    <div
      v-else
      class="v-hero__image"
      :style="[
        !!imgSrc && {backgroundImage: `url('${resolveUrl(imgSrc)}')`},
        !!imgAlign && {backgroundPosition: imgAlign},
        (!!imgMdSrc || !!imgSrc) && {'--hero-image-large': `url('${resolveUrl(imgMdSrc || imgSrc)}')`}
      ]"
    >
      <div v-if="hasContent" class="v-hero__content container-fluid">
        <slot />
      </div>
    </div>
  </header>
</template>

<script>
export default {
  props: {
    imgSrc: {
      type: String
    },
    imgMdSrc: {
      type: String
    },
    imgAlign: {
      type: String
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    minimal: {
      type: Boolean,
      default: false
    },
    split: {
      type: Boolean,
      default: false
    },
    video: {
      type: Boolean,
      default: false
    },
    blur: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasContent() {
      return !!this.$slots.default;
    }
  },
  methods: {
    resolveUrl(url) {
      if (/^(?!https?|\:\/\/)/.test(url) && typeof document !== "undefined") {
        // Create link element
        const link = document.createElement("a");

        // Set relative
        link.href = url;

        // Return resolved URL
        return link.href;
      }

      // Fallback to provided URL
      return url;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-hero {
  position: relative;
  background-color: #f1f1f1;

  &__image {
    position: relative;
    margin: 0 auto;
    max-width: 1680px;
    background-size: cover;
    background-position: center;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(md) {
      @media (min-resolution: 2dppx) {
        background-image: var(--hero-image-large) !important;
      }
    }

    @include media-breakpoint-up(md) {
      min-height: 500px;
    }

    .v-hero--auto-height & {
      min-height: 0;
    }

    .v-hero--small & {
      @include media-breakpoint-up(md) {
        min-height: 400px;
      }
    }

    .v-hero--with-image & {
      &::before {
        content: "";
        display: block;
        background-color: rgba(242, 242, 244, 0.85);
        @include media-breakpoint-up(lg) {
          background-color: transparent;
          background-image: linear-gradient(
            90deg,
            rgba(241, 241, 241, 1) 0%,
            rgba(241, 241, 241, 0.95) 5%,
            rgba(216, 216, 216, 0) 80%
          );
        }
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }

    .v-hero--extra-blur & {
      &::before {
        background-color: rgba(242, 242, 244, 0.9);
        @include media-breakpoint-up(lg) {
          background-color: transparent;
          background-image: linear-gradient(
            90deg,
            rgba(241, 241, 241, 1) 0%,
            rgba(241, 241, 241, 0.9) 50%,
            rgba(216, 216, 216, 0) 80%
          );
        }
      }
    }

    @media (min-width: 991px) {
      &::after {
        content: "";
        display: block;
        background-color: rgba(242, 242, 244, 0.85);
        @include media-breakpoint-up(lg) {
          background-color: transparent;
          background-image: linear-gradient(
            -90deg,
            rgba(241, 241, 241, 1) 0%,
            rgba(241, 241, 241, 0.98) 5%,
            rgba(216, 216, 216, 0) 80%
          );
        }
        position: absolute;
        top: 0;
        right: 0;
        width: 40%;
        height: 100%;
      }
    }
  }

  &__content {
    position: relative;
    z-index: 2;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: $spacer * 5 2.5rem !important;

    @include media-breakpoint-up(md) {
      padding: $spacer * 5 2.5rem !important;
    }

    .v-hero--minimal & {
      padding: $spacer * 3 1rem 0 1rem !important;

      @include media-breakpoint-up(md) {
        padding: $spacer * 6 1.5rem 0 1.5rem !important;
      }
    }
  }

  &--minimal {
    background-color: transparent;
    background-image: linear-gradient(
      180deg,
      rgba(250, 250, 250, 1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &--split {
    background-color: theme-color("white");

    > div {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid theme-color("light");

      @include media-breakpoint-up(md) {
        flex-direction: row-reverse;
      }
    }

    &__media {
      display: flex;
      align-items: center;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      padding-top: 100%;

      @include media-breakpoint-up(sm) {
        margin: 0 $spacer * 5;
        padding-top: calc(100% - #{$spacer * 10});
      }

      @include media-breakpoint-up(md) {
        background-origin: content-box;
        padding: 0 $spacer;
        margin: 0;
        min-width: 40%;
      }

      @include media-breakpoint-up(lg) {
        min-width: 50%;
      }

      &--video {
        padding-top: $spacer;

        @include media-breakpoint-up(md) {
          padding-top: 0;
        }

        >>> .v-embed,
        >>> .v-captureclick {
          width: 100%;
        }
      }
    }

    &__content {
      display: flex;
      align-items: center;
      padding: $spacer * 1 0;
      flex-grow: 1;

      @include media-breakpoint-up(md) {
        padding: $spacer * 5 1rem;
      }
    }
  }
}
</style>
